import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  useAdminCommunities,
  useAdminCommunityActions,
  useAdminCommunityMembers,
  useAdminUpdateMemberRole,
  useAdminBanCommunityMember,
  useAdminRemoveCommunityMember,
} from '../../hooks/use-admin';
import {
  Users,
  MessageSquare,
  Settings,
  Shield,
  Eye,
  EyeOff,
  Trash2,
  AlertTriangle,
  Calendar,
  Crown,
  Edit,
  Search,
  ExternalLink,
  UserMinus,
  Ban,
  RefreshCw,
  ShieldCheck,
  UserCog,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';

// ===================== MEMBER MANAGEMENT DIALOG ===================== //

function MemberManagementDialog({ community, trigger }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [actionDialog, setActionDialog] = useState({
    open: false,
    type: null,
    member: null,
  });
  const [actionReason, setActionReason] = useState('');
  const [selectedRole, setSelectedRole] = useState('');

  const { data, isLoading, refetch } = useAdminCommunityMembers(
    isOpen ? community.oid : null,
    {
      search: searchQuery || undefined,
      role: roleFilter !== 'all' ? roleFilter : undefined,
    }
  );

  const updateRole = useAdminUpdateMemberRole();
  const banMember = useAdminBanCommunityMember();
  const removeMember = useAdminRemoveCommunityMember();

  const handleAction = (type, member) => {
    setActionDialog({ open: true, type, member });
    if (type === 'role') {
      setSelectedRole(member.role);
    }
  };

  const confirmAction = async () => {
    const { type, member } = actionDialog;
    try {
      if (type === 'role') {
        await updateRole.mutateAsync({
          communityOid: community.oid,
          userOid: member.userOid,
          role: selectedRole,
          reason: actionReason,
        });
      } else if (type === 'ban') {
        await banMember.mutateAsync({
          communityOid: community.oid,
          userOid: member.userOid,
          ban: !member.isBanned,
          reason: actionReason,
        });
      } else if (type === 'remove') {
        await removeMember.mutateAsync({
          communityOid: community.oid,
          userOid: member.userOid,
          reason: actionReason,
        });
      }
      refetch();
    } catch (error) {
      console.error('Action failed:', error);
    }
    setActionDialog({ open: false, type: null, member: null });
    setActionReason('');
    setSelectedRole('');
  };

  const getRoleBadge = (role) => {
    const colors = {
      owner: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      superAdmin: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      admin: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      moderator: 'bg-green-500/20 text-green-400 border-green-500/30',
      member: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30',
    };
    const labels = {
      owner: 'Owner',
      superAdmin: 'Super Admin',
      admin: 'Admin',
      moderator: 'Moderator',
      member: 'Member',
    };
    return (
      <Badge className={colors[role] || colors.member}>
        {labels[role] || role}
      </Badge>
    );
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>{trigger}</DialogTrigger>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-4xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Users className="w-5 h-5" />
              Manage Members: {community.name}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Global admin view - You can moderate all members including super
              admins
            </DialogDescription>
          </DialogHeader>

          {/* Filters */}
          <div className="flex gap-4 py-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
              <Input
                placeholder="Search members..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-[150px] bg-zinc-800 border-zinc-700 text-white">
                <SelectValue placeholder="All Roles" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="owner">Owner</SelectItem>
                <SelectItem value="superAdmin">Super Admin</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="moderator">Moderator</SelectItem>
                <SelectItem value="member">Member</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="icon"
              onClick={() => refetch()}
              className="border-zinc-700"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>

          {/* Members Table */}
          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="h-6 w-6 animate-spin text-zinc-500" />
              </div>
            ) : !data?.members || data.members.length === 0 ? (
              <div className="text-center py-12 text-zinc-500">
                No members found
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800">
                    <TableHead className="text-zinc-400">Member</TableHead>
                    <TableHead className="text-zinc-400">Role</TableHead>
                    <TableHead className="text-zinc-400">Status</TableHead>
                    <TableHead className="text-zinc-400">Joined</TableHead>
                    <TableHead className="text-zinc-400 text-right">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.members.map((member) => (
                    <TableRow key={member.wallet} className="border-zinc-800">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          {member.avatar ? (
                            <img
                              src={member.avatar}
                              alt={member.username}
                              className="w-8 h-8 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-zinc-700 flex items-center justify-center">
                              <Users className="w-4 h-4 text-zinc-400" />
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-white">
                              {member.username}
                            </p>
                            <p className="text-xs text-zinc-500 font-mono">
                              {member.wallet?.slice(0, 6)}...
                              {member.wallet?.slice(-4)}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{getRoleBadge(member.role)}</TableCell>
                      <TableCell>
                        {member.isBanned ? (
                          <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                            Banned
                          </Badge>
                        ) : (
                          <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                            Active
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-zinc-400 text-sm">
                        {member.joinedAt
                          ? new Date(member.joinedAt).toLocaleDateString()
                          : 'Unknown'}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-zinc-400 hover:text-white"
                            >
                              <UserCog className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            className="bg-zinc-900 border-zinc-800"
                            align="end"
                          >
                            <DropdownMenuItem
                              className="text-zinc-300 hover:text-white cursor-pointer"
                              onClick={() => handleAction('role', member)}
                            >
                              <ShieldCheck className="w-4 h-4 mr-2" />
                              Change Role
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className={`cursor-pointer ${
                                member.isBanned
                                  ? 'text-green-400'
                                  : 'text-yellow-400'
                              }`}
                              onClick={() => handleAction('ban', member)}
                            >
                              <Ban className="w-4 h-4 mr-2" />
                              {member.isBanned ? 'Unban Member' : 'Ban Member'}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator className="bg-zinc-800" />
                            {member.role !== 'owner' && (
                              <DropdownMenuItem
                                className="text-red-400 cursor-pointer"
                                onClick={() => handleAction('remove', member)}
                              >
                                <UserMinus className="w-4 h-4 mr-2" />
                                Remove from Community
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>

          {/* Pagination info */}
          {data?.pagination && (
            <div className="text-sm text-zinc-500 pt-2 border-t border-zinc-800">
              Showing {data.members?.length || 0} of {data.pagination.total}{' '}
              members
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Action Confirmation Dialog */}
      <Dialog
        open={actionDialog.open}
        onOpenChange={(open) =>
          !open && setActionDialog({ open: false, type: null, member: null })
        }
      >
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-white">
              {actionDialog.type === 'role' && 'Change Member Role'}
              {actionDialog.type === 'ban' &&
                (actionDialog.member?.isBanned ? 'Unban Member' : 'Ban Member')}
              {actionDialog.type === 'remove' && 'Remove Member'}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              {actionDialog.type === 'role' &&
                `Change the role for ${actionDialog.member?.username}`}
              {actionDialog.type === 'ban' &&
                (actionDialog.member?.isBanned
                  ? `Are you sure you want to unban ${actionDialog.member?.username}?`
                  : `Are you sure you want to ban ${actionDialog.member?.username}?`)}
              {actionDialog.type === 'remove' &&
                `Are you sure you want to remove ${actionDialog.member?.username} from this community?`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            {actionDialog.type === 'role' && (
              <div>
                <Label className="text-zinc-300">New Role</Label>
                <Select value={selectedRole} onValueChange={setSelectedRole}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="owner">Owner</SelectItem>
                    <SelectItem value="superAdmin">Super Admin</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="moderator">Moderator</SelectItem>
                    <SelectItem value="member">Member</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label className="text-zinc-300">
                Reason{' '}
                {actionDialog.type !== 'role' && (
                  <span className="text-zinc-500">(optional)</span>
                )}
              </Label>
              <Textarea
                value={actionReason}
                onChange={(e) => setActionReason(e.target.value)}
                placeholder="Reason for this action..."
                rows={2}
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
              />
            </div>

            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setActionDialog({ open: false, type: null, member: null });
                  setActionReason('');
                  setSelectedRole('');
                }}
                className="border-zinc-700"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmAction}
                disabled={actionDialog.type === 'role' && !selectedRole}
                className={
                  actionDialog.type === 'remove'
                    ? 'bg-red-600 hover:bg-red-700'
                    : actionDialog.type === 'ban' &&
                      !actionDialog.member?.isBanned
                    ? 'bg-yellow-600 hover:bg-yellow-700'
                    : 'bg-indigo-600 hover:bg-indigo-700'
                }
              >
                {actionDialog.type === 'role' && 'Update Role'}
                {actionDialog.type === 'ban' &&
                  (actionDialog.member?.isBanned ? 'Unban' : 'Ban')}
                {actionDialog.type === 'remove' && 'Remove'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ===================== COMMUNITY ACTIONS ===================== //

function CommunityActions({ community, onUpdate }) {
  const { suspendCommunity, deleteCommunity, updateCommunity } =
    useAdminCommunityActions();
  const [isEditing, setIsEditing] = useState(false);
  const [isSuspendOpen, setIsSuspendOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [formData, setFormData] = useState({
    name: community.name || '',
    description: community.description || '',
    isPublic: community.isPublic || false,
  });

  const handleSuspend = () => {
    suspendCommunity({
      communityOid: community.oid,
      suspend: !community.isSuspended,
      reason:
        suspendReason ||
        (community.isSuspended ? 'Unsuspended by admin' : 'Suspended by admin'),
    });
    setIsSuspendOpen(false);
    setSuspendReason('');
    onUpdate();
  };

  const handleDelete = () => {
    if (!deleteReason.trim()) return;
    deleteCommunity({ communityOid: community.oid, reason: deleteReason });
    setIsDeleteOpen(false);
    setDeleteReason('');
    onUpdate();
  };

  const handleUpdate = async () => {
    updateCommunity({ communityOid: community.oid, data: formData });
    setIsEditing(false);
    onUpdate();
  };

  return (
    <div className="flex items-center gap-2">
      {/* Edit Dialog */}
      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="border-zinc-700 hover:bg-zinc-800"
          >
            <Edit className="w-4 h-4" />
          </Button>
        </DialogTrigger>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-white">
              Edit Community: {community.name}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Update community settings and information.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-zinc-300">Community Name</Label>
              <Input
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                placeholder="Community name"
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-zinc-300">Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                placeholder="Community description"
                rows={3}
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                checked={formData.isPublic}
                onCheckedChange={(checked) =>
                  setFormData((prev) => ({ ...prev, isPublic: checked }))
                }
              />
              <Label className="text-zinc-300">Public Community</Label>
            </div>
            <div className="flex gap-2 pt-2">
              <Button
                onClick={handleUpdate}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                Save Changes
              </Button>
              <Button
                variant="outline"
                onClick={() => setIsEditing(false)}
                className="border-zinc-700"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Suspend Dialog */}
      <Dialog open={isSuspendOpen} onOpenChange={setIsSuspendOpen}>
        <DialogTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={
              community.isSuspended
                ? 'text-green-500 border-green-500/30 hover:bg-green-500/10'
                : 'text-yellow-500 border-yellow-500/30 hover:bg-yellow-500/10'
            }
          >
            {community.isSuspended ? (
              <Eye className="w-4 h-4" />
            ) : (
              <EyeOff className="w-4 h-4" />
            )}
          </Button>
        </DialogTrigger>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              {community.isSuspended ? (
                <>
                  <Eye className="w-5 h-5 text-green-500" />
                  Unsuspend Community
                </>
              ) : (
                <>
                  <EyeOff className="w-5 h-5 text-yellow-500" />
                  Suspend Community
                </>
              )}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              {community.isSuspended
                ? `Are you sure you want to unsuspend "${community.name}"? Members will be able to access the community again.`
                : `Are you sure you want to suspend "${community.name}"? Members will not be able to access the community while suspended.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label className="text-zinc-300">
                Reason{' '}
                {!community.isSuspended && (
                  <span className="text-zinc-500">(optional)</span>
                )}
              </Label>
              <Textarea
                value={suspendReason}
                onChange={(e) => setSuspendReason(e.target.value)}
                placeholder={
                  community.isSuspended
                    ? 'Reason for unsuspending...'
                    : 'Reason for suspension...'
                }
                rows={2}
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setIsSuspendOpen(false);
                  setSuspendReason('');
                }}
                className="border-zinc-700"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSuspend}
                className={
                  community.isSuspended
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-yellow-600 hover:bg-yellow-700'
                }
              >
                {community.isSuspended ? 'Unsuspend' : 'Suspend'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <DialogTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="text-red-500 border-red-500/30 hover:bg-red-500/10"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </DialogTrigger>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Delete Community
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Are you sure you want to delete "{community.name}"? This action
              cannot be undone. All posts, members, and data will be permanently
              removed.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label className="text-zinc-300">
                Reason for deletion <span className="text-red-400">*</span>
              </Label>
              <Textarea
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                placeholder="e.g., Violation of terms of service, spam community..."
                rows={2}
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setIsDeleteOpen(false);
                  setDeleteReason('');
                }}
                className="border-zinc-700"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDelete}
                disabled={!deleteReason.trim()}
                className="bg-red-600 hover:bg-red-700 disabled:opacity-50"
              >
                Delete Community
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CommunityCard({ community, onUpdate }) {
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <Card className="bg-zinc-900 border-zinc-800">
      <CardHeader className="pb-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            {community.profileImage && (
              <img
                src={community.profileImage}
                alt={community.name}
                className="w-10 h-10 rounded-lg object-cover"
              />
            )}
            <div>
              <CardTitle className="text-lg text-white">
                {community.name}
              </CardTitle>
              <CardDescription className="mt-1 text-zinc-400">
                Created {formatDate(community.createdAt)}
              </CardDescription>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            {community.isSuspended && (
              <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                Suspended
              </Badge>
            )}
            <Badge
              className={
                community.isPublic
                  ? 'bg-green-500/20 text-green-400'
                  : 'bg-zinc-500/20 text-zinc-400'
              }
            >
              {community.isPublic ? 'Public' : 'Private'}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <p className="text-sm text-zinc-400 line-clamp-2">
            {community.description || 'No description provided'}
          </p>

          <div className="flex items-center gap-4 text-sm text-zinc-500 flex-wrap">
            <div className="flex items-center gap-1">
              <Crown className="w-4 h-4 text-yellow-500" />
              <span>
                {community.ownerInfo?.username ||
                  (community.owner
                    ? `${community.owner.slice(0, 6)}...${community.owner.slice(
                        -4
                      )}`
                    : 'Unknown')}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Users className="w-4 h-4" />
              <span>{community.memberCount || 0} members</span>
            </div>
            <div className="flex items-center gap-1">
              <MessageSquare className="w-4 h-4" />
              <span>{community.discussionCount || 0} posts</span>
            </div>
          </div>
          <div className="flex justify-between items-center gap-2">
            <div className="flex items-center gap-2">
              <Link
                to={`/forum/${community.slug}`}
                target="_blank"
                className="inline-flex items-center gap-1 text-sm text-blue-400 hover:text-blue-300"
              >
                <ExternalLink className="w-3 h-3" />
                View Community
              </Link>
              <MemberManagementDialog
                community={community}
                trigger={
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-indigo-400 border-indigo-500/30 hover:bg-indigo-500/10"
                  >
                    <Users className="w-3 h-3 mr-1" />
                    Manage Members
                  </Button>
                }
              />
            </div>
            <CommunityActions community={community} onUpdate={onUpdate} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdminCommunities() {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, active, suspended
  const [filterType, setFilterType] = useState('all'); // all, public, private

  const { data, isLoading, refetch } = useAdminCommunities({
    search: searchTerm || undefined,
    status: filterStatus !== 'all' ? filterStatus : undefined,
    type: filterType !== 'all' ? filterType : undefined,
  });

  const communities = data?.communities || [];
  const stats = data?.stats || {
    total: 0,
    active: 0,
    suspended: 0,
    public: 0,
    private: 0,
  };

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-zinc-800 rounded w-1/4"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Users className="h-6 w-6" />
            Community Management
          </h1>
          <p className="text-zinc-400">
            Manage all communities on the platform
          </p>
        </div>
        <Button className="bg-indigo-600 hover:bg-indigo-700">
          <Shield className="w-4 h-4 mr-2" />
          Create Community
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="pb-2">
            <CardDescription className="text-zinc-400">
              Total Communities
            </CardDescription>
            <CardTitle className="text-2xl text-white">{stats.total}</CardTitle>
          </CardHeader>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="pb-2">
            <CardDescription className="text-green-400">Active</CardDescription>
            <CardTitle className="text-2xl text-white">
              {stats.active}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="pb-2">
            <CardDescription className="text-red-400">
              Suspended
            </CardDescription>
            <CardTitle className="text-2xl text-white">
              {stats.suspended}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="pb-2">
            <CardDescription className="text-blue-400">Public</CardDescription>
            <CardTitle className="text-2xl text-white">
              {stats.public}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="pb-2">
            <CardDescription className="text-zinc-400">Private</CardDescription>
            <CardTitle className="text-2xl text-white">
              {stats.private}
            </CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-white">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
              <Input
                placeholder="Search communities..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            <div className="flex gap-2">
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[140px] bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-[140px] bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="public">Public</SelectItem>
                  <SelectItem value="private">Private</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Communities Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {communities.map((community) => (
          <CommunityCard
            key={community.oid || community._id}
            community={community}
            onUpdate={refetch}
          />
        ))}
      </div>

      {communities.length === 0 && (
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="text-center py-8">
            <AlertTriangle className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white">
              No communities found
            </h3>
            <p className="text-zinc-400">
              {searchTerm || filterStatus !== 'all' || filterType !== 'all'
                ? 'Try adjusting your filters'
                : 'No communities have been created yet'}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
